import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  createReviewedUnclaimReviewController,
  type ReviewedUnclaimReviewController,
} from '../../services/reviewedUnclaimReviewService';

interface Props {
  open: boolean;
  taskId: string;
  transport: (payload: unknown) => Promise<unknown>;
  onClose: () => void;
  onPrepared: (controller: ReviewedUnclaimReviewController) => void;
}

const validOperationKey = (value: string) => /^[a-zA-Z0-9_-]{8,128}$/.test(value);
const validHash = (value: string) => /^[a-f0-9]{64}$/.test(value);

const ReviewedUnclaimPrepareDialog: React.FC<Props> = ({ open, taskId, transport, onClose, onPrepared }) => {
  const [operationKey, setOperationKey] = useState('');
  const [inventoryHash, setInventoryHash] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [attemptLocked, setAttemptLocked] = useState(false);
  const attempt = useRef<{ operationKey: string; inventoryHash: string; controller: ReviewedUnclaimReviewController } | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    attempt.current = null;
    setOperationKey('');
    setInventoryHash('');
    setBusy(false);
    setError(false);
    setAttemptLocked(false);
    return () => { generation.current += 1; attempt.current = null; };
  }, [open, taskId, transport]);

  const close = () => {
    if (busy) return;
    generation.current += 1;
    attempt.current = null;
    onClose();
  };

  const prepare = async () => {
    if (!validOperationKey(operationKey) || !validHash(inventoryHash)) return;
    const requestGeneration = generation.current;
    const isCurrent = () => generation.current === requestGeneration;
    let currentAttempt = attempt.current;
    if (!currentAttempt) {
      try {
        const controller = createReviewedUnclaimReviewController(transport, {
          taskId, operationKey, frozenInventorySha256: inventoryHash,
        });
        currentAttempt = { operationKey, inventoryHash, controller };
        attempt.current = currentAttempt;
      } catch {
        setError(true);
        return;
      }
      setAttemptLocked(true);
    }
    setBusy(true);
    setError(false);
    try {
      await currentAttempt.controller.prepare();
      if (!isCurrent()) return;
      await currentAttempt.controller.preview();
      if (!isCurrent()) return;
      onPrepared(currentAttempt.controller);
    } catch {
      if (isCurrent()) setError(true);
    } finally {
      if (isCurrent()) setBusy(false);
    }
  };

  const valid = validOperationKey(operationKey) && validHash(inventoryHash);
  return (
    <Dialog open={open} onClose={close} disableEscapeKeyDown={busy} maxWidth="sm" fullWidth aria-labelledby="reviewed-unclaim-prepare-title">
      <DialogTitle id="reviewed-unclaim-prepare-title">Prepare reviewed unclaim</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="info">
            Enter the operation key and inventory hash from the reviewed maintenance window. These values identify the frozen review; this form does not create a baseline or freeze.
          </Alert>
          {error && <Alert severity="error">Could not prepare a current reviewed-unclaim plan. Retry with the same values or close this review.</Alert>}
          <TextField
            label="Maintenance operation key"
            value={operationKey}
            onChange={event => setOperationKey(event.target.value)}
            disabled={busy || attemptLocked}
            inputProps={{ maxLength: 128 }}
            helperText="Use the operation key recorded for the reviewed maintenance window."
          />
          <TextField
            label="Frozen inventory SHA-256"
            value={inventoryHash}
            onChange={event => setInventoryHash(event.target.value)}
            disabled={busy || attemptLocked}
            inputProps={{ maxLength: 64 }}
            helperText="64 lowercase hexadecimal characters from the same reviewed window."
          />
          <Typography variant="body2">Task: {taskId}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Close</Button>
        <Button variant="contained" onClick={prepare} disabled={busy || !valid}>
          {busy ? <CircularProgress size={20} aria-label="Preparing review" /> : attemptLocked ? 'Retry prepare' : 'Prepare and preview'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewedUnclaimPrepareDialog;
